import { Address } from "../../services";
import { createSessionStore } from "../session-storage/create-session-store";

type PatientPortalProfileData = {
  firstName?: string;
  lastName?: string;
  mobileNumber?: string | null;
  phone?: string | null;
  birthDate?: string | null;
  email?: string | null;
  gender?: string | null;
  languages?: string[] | Record<string, string> | null;
  ethnicity?: string | null;
  ssn?: string | null;
  race?: string | null;
  avatar?: string | null;
  emergencyContacts?: {
    firstName?: string | null;
    lastName?: string | null;
    mobile?: string | null;
    email?: string | null;
    relationshipWithPatient?: string | null;
  }[];
};

export interface CurrentUser extends PatientPortalProfileData {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  birthDate: string;
  avatar: null | string;
  active: boolean;
  source: null | string;
  archive: boolean;
  timezone: string;
  providerType: string;
  pgName?: string;
  gender: "MALE" | "FEMALE" | "OTHER" | "UNSPECIFIED";
  npi: number;
  federalTaxIdNumber: null | string;
  role: "PROVIDER";
  roleType: "PROVIDER" | "STAFF";
  officeFaxNumber: string;
  licenseNumber: null | string;
  groupNpiNumber: number;
  taxonomyNumber: string;
  workLocations: Record<string, string>;
  acceptedInsurances: Record<string, unknown>;
  specialities: Record<string, string>;
  licenceInformation: {
    uuid: string;
    licenseState: string;
    licenseNumber: string;
    expiryDate: string;
    licenseActiveDate: string;
    licenseDocument: null | string;
    licenseName: null | string;
  }[];
  deaInformation: {
    uuid: string;
    deaState: string;
    deaNumber: string;
    deaTermDate: string;
    deaActiveDate: string;
  }[];
  experience: string;
  bio: string;
  errorMessage: null | string;
  referralNumber: null | string;
  spokenLanguages: Record<string, unknown>;
  lastLogin: string;
  colorCode: string;
  loginUrl: null | string;
  isReferral: null | boolean;
  address: null | Address;
  practiceName: null | string;
  temporaryEmail: null | string;
  emailVerified: boolean;
  twoFAEnabled: boolean;
  twoFAType: "GOOGLE_AUTHENTICATOR" | "EMAIL" | "SMS";
  /** `{ [cosignerUuid]: cosignerName }` for providers that require a co-signature. */
  cosigner?: Record<string, string>;

  /**
   * @see [Todo]
   * Add as per Requirement
   *
   */
}

type Action = [CurrentUser | undefined, (v: CurrentUser) => void];

/**
 * Single source of truth for the logged-in user.
 *
 * The user lives in sessionStorage so each tab keeps its own identity alongside
 * its own tokens — see utils/auth-storage.ts
 */
const userStore = createSessionStore<CurrentUser | undefined>(
  "user",
  undefined
);

/**
 * Writes the user from outside a component (login, portal bootstrap).
 * Always prefer this over sessionStorage.setItem("user") — a raw write leaves
 * the cache holding the previous value and mounted readers never update.
 */
export const setCurrentUser = userStore.setValue;

const useCurrentUser = (): Action => {
  const [me, setMe] = userStore.useStore();

  return [me, setMe];
};

export default useCurrentUser;
